/**
 * Regional Settings Tab Component
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { MapPin, Building2, AlertCircle, Save, Loader2 } from 'lucide-react';
import { SiteRegionalSettings, PreferencesChoices } from './types';

interface RegionalTabProps {
  siteSettings: SiteRegionalSettings[];
  choices: PreferencesChoices | null;
  loadingSites: boolean;
  savingSite: number | null;
  editedSites: { [key: number]: Partial<SiteRegionalSettings> };
  onSiteSettingChange: (siteId: number, field: string, value: string | null) => void;
  onSaveSiteSettings: (siteId: number) => void;
}

export function RegionalTab({
  siteSettings,
  choices,
  loadingSites,
  savingSite,
  editedSites,
  onSiteSettingChange,
  onSaveSiteSettings,
}: RegionalTabProps) {
  return (
    <div className="space-y-4">
      {/* Site Regional Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Site Regional Settings
          </CardTitle>
          <CardDescription>
            Configure regional settings for each site. These settings override company defaults for users at each site.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingSites ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : siteSettings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No companies found. Companies need to have regional settings configured.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {siteSettings.map((site) => {
                const hasEdits = !!editedSites[site.id];
                const currentValues = {
                  ...site,
                  ...editedSites[site.id],
                };

                return (
                  <div key={site.id} className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <h4 className="font-semibold">{site.name || site.site_name}</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasEdits && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            Unsaved Changes
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          onClick={() => onSaveSiteSettings(site.id)}
                          disabled={!hasEdits || savingSite === site.id}
                        >
                          {savingSite === site.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1" />
                          ) : (
                            <Save className="h-4 w-4 mr-1" />
                          )}
                          Save
                        </Button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Currency */}
                      <div className="space-y-2">
                        <Label htmlFor={`currency-${site.id}`}>Currency</Label>
                        <select
                          id={`currency-${site.id}`}
                          value={currentValues.default_currency || ''}
                          onChange={(e) => onSiteSettingChange(site.id, 'default_currency', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        >
                          <option value="">Inherit from Company</option>
                          {choices?.currencies.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Timezone */}
                      <div className="space-y-2">
                        <Label htmlFor={`timezone-${site.id}`}>Timezone</Label>
                        <select
                          id={`timezone-${site.id}`}
                          value={currentValues.default_timezone || ''}
                          onChange={(e) => onSiteSettingChange(site.id, 'default_timezone', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        >
                          <option value="">Inherit from Company</option>
                          {choices?.timezones.map((tz) => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Language */}
                      <div className="space-y-2">
                        <Label htmlFor={`language-${site.id}`}>Language</Label>
                        <select
                          id={`language-${site.id}`}
                          value={currentValues.default_language || ''}
                          onChange={(e) => onSiteSettingChange(site.id, 'default_language', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        >
                          <option value="">Inherit from Company</option>
                          {choices?.languages.map((l) => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Date Format */}
                      <div className="space-y-2">
                        <Label htmlFor={`dateFormat-${site.id}`}>Date Format</Label>
                        <select
                          id={`dateFormat-${site.id}`}
                          value={currentValues.default_date_format || ''}
                          onChange={(e) => onSiteSettingChange(site.id, 'default_date_format', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        >
                          <option value="">Inherit from Company</option>
                          {choices?.date_formats.map((df) => (
                            <option key={df.value} value={df.value}>{df.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Decimal Separator */}
                      <div className="space-y-2">
                        <Label htmlFor={`decimalSep-${site.id}`}>Decimal Separator</Label>
                        <select
                          id={`decimalSep-${site.id}`}
                          value={currentValues.decimal_separator || ''}
                          onChange={(e) => onSiteSettingChange(site.id, 'decimal_separator', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        >
                          <option value="">Inherit from Company</option>
                          <option value=".">Period (.)</option>
                          <option value=",">Comma (,)</option>
                        </select>
                      </div>

                      {/* Thousands Separator */}
                      <div className="space-y-2">
                        <Label htmlFor={`thousandsSep-${site.id}`}>Thousands Separator</Label>
                        <select
                          id={`thousandsSep-${site.id}`}
                          value={currentValues.thousands_separator || ''}
                          onChange={(e) => onSiteSettingChange(site.id, 'thousands_separator', e.target.value)}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        >
                          <option value="">Inherit from Company</option>
                          <option value=",">Comma (,)</option>
                          <option value=".">Period (.)</option>
                          <option value=" ">Space ( )</option>
                        </select>
                      </div>
                    </div>

                    {/* Delivery Lines (ASN page) — per-site backlog cutoff */}
                    <div className="border-t pt-4 space-y-1">
                      <Label htmlFor={`dlHide-${site.id}`}>Hide delivery lines released before</Label>
                      <p className="text-xs text-muted-foreground">
                        Delivery lines released before this date are hidden from the ASN page&apos;s Delivery Lines tab for this site. Leave blank to show all.
                      </p>
                      <input
                        type="date"
                        id={`dlHide-${site.id}`}
                        value={(currentValues.delivery_lines_hidden_before as string | null) || ''}
                        onChange={(e) => onSiteSettingChange(site.id, 'delivery_lines_hidden_before', e.target.value || null)}
                        className="px-3 py-2 border border-border rounded-md bg-background"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Preferences Info */}
      <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Company Default Preferences
            <Badge variant="outline" className="ml-auto">Info</Badge>
          </CardTitle>
          <CardDescription>
            Company-level defaults are inherited by all sites unless overridden
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
            <AlertCircle className="h-4 w-4" />
            <p className="text-sm">
              Company preferences can be managed from the Company Settings section. Users can also override these settings in their personal preferences.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
